import {
  between, maxLength, minLength, integer, requiredIf,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { StringMap, TypeCampaignEnum } from '@/types';
import {
  checkIfTemplateOrRedirectContainsVoucher,
  getTemplateAndRedirect,
  reusePreviousCouponValues,
  voucherMethodsValues,
  voucherTypeValues,
  migrateOldVoucherConfig,
  buildTemplateVouchersRules,
  TemplateVouchersData,
  getDefaultEnabledOperator,
} from '@/composables/flows';
import { asInt } from '@/helpers';
import { getDefaultUtmFields } from '@/components/flows/operators/shared/utmDefaults';
import type { FlowOperatorMetadata, ValidationResult, WarningValidator, WarningValidationContext, NodeWarning } from '@/types/flow-types';
import Form from './Form.vue';
import { canDeleteSendingAction } from '@/components/flows/operators/registry';
import {
  WhatsappValidationStatus,
  isBlockingStatus,
  getStatusSeverity,
  getStatusMessageKey,
} from '@/composables/flows/useWhatsappTemplateValidation';

export interface SendWhatsappData {
  template_display_selector_id: number | null;
  reuse_a_previous_coupon: number;
  display_popup_on_site: number;
  template_display_reduction_method: number;
  template_display_reduction_descript: StringMap;
  template_display_reduction_type: number;
  template_display_reduction_identifiant: string;
  template_display_reduction_amount: number;
  template_display_reduction_duplicate: string;
  template_display_reduction_minorderamount: number;
  template_display_reduction_validtime: number;
  template_display_reduction_prefix: string;
  template_vouchers?: TemplateVouchersData;
  template_display_message_langs: string;
  template_validation_status: string;
  enabled_operator: number;
  [key: string]: any;
}

export enum SendWhatsappValidationStatusEnum {
  NOT_SUBMITTED = 'NOT_SUBMITTED',
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  IN_APPEAL = 'IN_APPEAL',
  PAUSED = 'PAUSED',
  DISABLED = 'DISABLED',
  FLAGGED = 'FLAGGED',
  ARCHIVED = 'ARCHIVED',
  DELETED = 'DELETED',
  PENDING_DELETION = 'PENDING_DELETION',
  LIMIT_EXCEEDED = 'LIMIT_EXCEEDED',
  LOCKED = 'LOCKED',
  REINSTATED = 'REINSTATED',
  ERROR = 'ERROR'
}

export function createDefaultData(data: Partial<SendWhatsappData> = {}, languages: string[] = [], campaignType?: string): SendWhatsappData {
  return {
    template_display_selector_id: data.template_display_selector_id != null ? asInt(data.template_display_selector_id) : null,
    reuse_a_previous_coupon: asInt(data.reuse_a_previous_coupon ?? 0),
    display_popup_on_site: asInt(data.display_popup_on_site ?? 0),
    template_display_reduction_method: asInt(data.template_display_reduction_method ?? 3),
    template_display_reduction_descript: data.template_display_reduction_descript ?? {},
    template_display_reduction_type: asInt(data.template_display_reduction_type ?? 1),
    template_display_reduction_identifiant: data.template_display_reduction_identifiant ?? '',
    template_display_reduction_amount: asInt(data.template_display_reduction_amount ?? 0),
    template_display_reduction_duplicate: data.template_display_reduction_duplicate ?? '',
    template_display_reduction_minorderamount: asInt(data.template_display_reduction_minorderamount ?? 0),
    template_display_reduction_validtime: asInt(data.template_display_reduction_validtime ?? 7),
    template_display_reduction_prefix: data.template_display_reduction_prefix ?? '',
    template_vouchers: data.template_vouchers && typeof data.template_vouchers === 'object' ? data.template_vouchers : {},
    template_display_message_langs: data.template_display_message_langs ?? JSON.stringify(languages),
    template_validation_status: data.template_validation_status ?? SendWhatsappValidationStatusEnum.NOT_SUBMITTED,
    enabled_operator: asInt(data.enabled_operator ?? getDefaultEnabledOperator('boxsendwhatsapp', campaignType, 0)),
    ...getDefaultUtmFields(data),
  };
}

interface ValidateContext {
  shopId: number;
}

/**
 * WhatsApp template not yet submitted for validation
 * Shows a warning with a submit button when template has not been submitted
 */
const whatsappNotSubmittedValidator: WarningValidator<SendWhatsappData> = {
  id: 'whatsapp-not-submitted',
  validate: (data: SendWhatsappData): NodeWarning | null => {
    const status = data.template_validation_status;

    // Only show when status is NOT_SUBMITTED or absent, and a template is selected
    if (!data.template_display_selector_id || data.template_display_selector_id <= 0) {
      return null;
    }

    if (status && status !== WhatsappValidationStatus.NOT_SUBMITTED) {
      return null;
    }

    return {
      id: 'whatsapp-not-submitted',
      severity: 'warning',
      location: 'box',
      message: 'whatsappValidation.warnings.notSubmitted',
      blocking: true,
      showInForm: true,
      actions: [
        {
          id: 'submit-whatsapp-validation',
          label: 'whatsappValidation.actions.submitForValidation',
          event: 'submit-whatsapp-validation',
          icon: 'far fa-paper-plane',
        },
      ],
    };
  },
};

/**
 * WhatsApp template validation status validator
 * Shows warning for PENDING status and error for REJECTED/ERROR status
 */
const whatsappValidationStatusValidator: WarningValidator<SendWhatsappData> = {
  id: 'whatsapp-validation-status',
  validate: (data: SendWhatsappData, context: WarningValidationContext): NodeWarning | null => {
    const status = data.template_validation_status;
    
    // No warning for NOT_SUBMITTED or APPROVED (NOT_SUBMITTED is handled by whatsappNotSubmittedValidator)
    if (!status || status === WhatsappValidationStatus.NOT_SUBMITTED || status === WhatsappValidationStatus.APPROVED) {
      return null;
    }

    const severity = getStatusSeverity(status);
    if (!severity) return null;

    // Use bulk-specific message keys (without "operator cannot be enabled" part)
    const isBulk = context.scenarioType === TypeCampaignEnum.BULK;
    const messageKey = isBulk ? getStatusMessageKey(status, true) : getStatusMessageKey(status);

    return {
      id: 'whatsapp-validation-status',
      severity,
      location: 'box',
      message: messageKey,
      blocking: isBlockingStatus(status),
    };
  },
};

/**
 * WhatsApp template approved/sendable info validator
 * Shows a non-blocking info message when the template is approved and ready to send
 */
const whatsappApprovedInfoValidator: WarningValidator<SendWhatsappData> = {
  id: 'whatsapp-approved-info',
  validate: (data: SendWhatsappData): NodeWarning | null => {
    const status = data.template_validation_status;

    if (!status || !data.template_display_selector_id || data.template_display_selector_id <= 0) {
      return null;
    }

    if (status === WhatsappValidationStatus.APPROVED || status === WhatsappValidationStatus.REINSTATED) {
      return {
        id: 'whatsapp-approved-info',
        severity: 'info',
        location: 'box',
        message: `whatsappValidation.warnings.${status.toLowerCase()}`,
        blocking: false,
      };
    }

    return null;
  },
};

const SendWhatsapp: FlowOperatorMetadata<SendWhatsappData> = {
  id: 'boxsendwhatsapp',
  kind: 'action',
  label: 'automatedScenarios.actions.sendWhatsapp.label',
  icon: 'fa-comment-alt-dots',
  hasConfiguration: true,
  availableIn: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
  canToggleEnabled: true,
  requiredServices: ['WhatsappBusinessServiceParameter'],

  validators: [whatsappNotSubmittedValidator, whatsappValidationStatusValidator, whatsappApprovedInfoValidator],

  menuActions: [
    {
      id: 'send-test-whatsapp',
      label: 'campaigns.common.operatorActions.sendTestMessage',
      icon: 'far fa-paper-plane',
      action: 'send-test-whatsapp',
    },
  ],

  incompatibilities: {
    [TypeCampaignEnum.AUTOMATION]: {
      nextOperators: [
        'boxabandonpanier', 'boxsitepasstime', 'boxquitsite',
        'boxperiodiclaunching', 'boxscrollpage', 'boxstatuscommand',
        'boxcustomjs', 'registeronsite', 'subscribetonewsletter',
        'boxsendwhatsapp', 'boxdisplayfreepopup', 'boxdisplayfreemodel',
        'boxvisitpage',
      ],
    },
    [TypeCampaignEnum.BULK]: {
      nextOperators: [
        'boxabandonpanier', 'boxsitepasstime', 'boxquitsite',
        'boxperiodiclaunching', 'boxscrollpage', 'boxstatuscommand',
        'boxcustomjs', 'registeronsite', 'subscribetonewsletter',
        'boxsendwhatsapp', 'boxdisplayfreepopup', 'boxdisplayfreemodel',
        'boxvisitpage', 'boxchoosebulklist', 'boxaudiencesetup',
      ],
    },
  },

  Form,

  defaultData: (data?: Partial<SendWhatsappData>, campaignType?: string) => createDefaultData(data, [], campaignType),

  outputs: () => 1,

  async validate(data: SendWhatsappData, context?: ValidateContext): Promise<ValidationResult> {
    let mustConfigVoucher = false;
    let mustConfigMultiVoucher = false;
    const templatesWithVoucher: string[] = [];

    if (data.template_display_selector_id && data.template_display_selector_id > 0 && context?.shopId) {
      const idTemplate = asInt(data.template_display_selector_id);
      let templatesContainsVoucher = false;
      const listTemplate = await getTemplateAndRedirect(context.shopId, idTemplate);
      if (listTemplate !== null) {
        templatesContainsVoucher = checkIfTemplateOrRedirectContainsVoucher(listTemplate);

        // Check if any template has hasVoucher = true in configuration
        listTemplate.forEach((template) => {
          if (typeof template.template_config === 'string') {
            const templateConfig = JSON.parse(template.template_config);
            if (templateConfig.hasVoucher) {
              templatesWithVoucher.push(String(template.id_template));
            }
            // Clean up stale disabled flag if template is no longer conditional
            const tId = String(template.id_template);
            if (data.template_vouchers?.[tId]?.disabled && !templateConfig.hasConditionalVoucher) {
              data.template_vouchers[tId].disabled = false;
            }
          }
        });
      }
      // For multi-voucher system: require config if any template has hasVoucher = true
      if (templatesWithVoucher.length > 0) {
        mustConfigMultiVoucher = true;
      }
      if (templatesContainsVoucher && data.reuse_a_previous_coupon === reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER) {
        mustConfigVoucher = true;
      }
    }

    // Always call this to ensure obsolete configs are removed when templates no longer have vouchers
    migrateOldVoucherConfig(data, templatesWithVoucher);

    const rules: any = {
      template_display_reduction_method: {
        required: mustConfigVoucher,
      },
      template_display_reduction_descript: {
        required: false,
      },
      template_display_reduction_type: {
        required: mustConfigVoucher,
      },
      template_display_reduction_amount: {
        required: requiredIf(() => mustConfigVoucher && [voucherTypeValues.PERCENTAGE, voucherTypeValues.AMOUNT].includes(data.template_display_reduction_type)),
        integer,
      },
      template_display_reduction_minorderamount: {
        required: requiredIf(() => mustConfigVoucher && data.template_display_reduction_method === voucherMethodsValues.AUTOMATIC),
        between: between(0, 10000000),
      },
      template_display_reduction_prefix: {
        minLength: minLength(2),
        maxLength: maxLength(32),
      },
    };

    if (mustConfigVoucher && data.template_display_reduction_method === voucherMethodsValues.MANUAL) {
      rules.template_display_reduction_identifiant = {
        required: true,
        minLength: minLength(2),
        maxLength: maxLength(45),
      };
    }

    if (mustConfigVoucher && data.template_display_reduction_method === voucherMethodsValues.AUTOMATIC && data.template_display_reduction_type === voucherTypeValues.DUPLICATED) {
      rules.template_display_reduction_duplicate = {
        required: true,
        minLength: minLength(2),
        maxLength: maxLength(45),
      };
    }

    if (mustConfigVoucher && data.template_display_reduction_method === voucherMethodsValues.AUTOMATIC) {
      rules.template_display_reduction_validtime = {
        required: true,
        between: between(1, 182),
      };
    }

    // Add dynamic validation rules for template_vouchers (only if multi-voucher system is used)
    if (mustConfigMultiVoucher) {
      Object.assign(rules, buildTemplateVouchersRules(data.template_vouchers, templatesWithVoucher));
    }

    const v$ = useVuelidate(rules, data);
    const success = await v$.value.$validate();

    return {
      success,
      validate: v$,
    };
  },

  async callback(data: SendWhatsappData): Promise<SendWhatsappData> {
    // Validation submission is now manual (triggered by the user via the warning action button)
    // Set status to NOT_SUBMITTED if no status exists yet
    if (!data.template_validation_status) {
      data.template_validation_status = SendWhatsappValidationStatusEnum.NOT_SUBMITTED;
    }
    return data;
  },

  canBeDeleted(operatorId: string, flow: any): Promise<boolean | string> {
    return canDeleteSendingAction(flow, operatorId);
  },
};

export default SendWhatsapp;
